import {HttpClient} from '@angular/common/http';
import {computed, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {Status} from '@domain/response-status';
import {User} from '@domain/user';
import {UserState} from '@domain/user-state';
import {TranslateService} from '@ngx-translate/core';
import {catchError, EMPTY, map, Subject, switchMap, tap} from 'rxjs';
import {SnackBarService} from 'src/shared/snackbar/snackbar.service';
import {RequestStatus} from '../request-status.enum';

@Injectable({
    providedIn: 'root'
})
export class AdminService
{
    private readonly httpClient = inject(HttpClient);

    private readonly translate = inject(TranslateService);

    private readonly snackBar = inject(SnackBarService);

    private readonly router = inject(Router);

    private readonly state = signal<UserState<User>>({
        status: RequestStatus.NOT_EXECUTED,
        user: null
    });

    private readonly passwordState = signal<Status>({
        status: RequestStatus.NOT_EXECUTED
    });

    public readonly admin = computed(() => this.state().user);

    public readonly loading = computed(() => this.state().status === RequestStatus.PENDING);

    private readonly get$ = new Subject<void>();

    public readonly adminFetched$ = this.get$.pipe(
        switchMap(() =>
        {
            return this.httpClient.get('admin').pipe(
                map(User.fromJSON),
                catchError(() =>
                {
                    this.state.set({
                        status: RequestStatus.ERROR,
                        user: null
                    });

                    return EMPTY;
                })
            );
        })
    );

    private readonly update$ = new Subject<Partial<User>>();

    private readonly adminUpdated$ = this.update$.pipe(
        switchMap((admin) =>
        {
            return this.httpClient.patch('admin', admin).pipe(
                catchError(() =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.ERROR,
                        user: this.admin()
                    });
                    
                    this.snackBar.showErrorSnackbar(this.translate.instant('PROFILE.ERROR_MESSAGE'));

                    return EMPTY;
                }),
                tap(() =>
                {
                    this.state.set({
                        ...this.state(),
                        user: new User(
                            this.admin().id,
                            this.admin().email,
                            admin.firstName,
                            admin.lastName,
                            admin.picture
                        )
                    });
                }));
        })
    );

    public constructor()
    {
        this.adminFetched$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: (user: User) =>
                {
                    this.state.set({
                        status: RequestStatus.CONCLUDED,
                        user
                    });
                }
            });

        this.adminUpdated$
            .pipe(takeUntilDestroyed())
            .subscribe({
                next: () =>
                {
                    this.state.set({
                        ...this.state(),
                        status: RequestStatus.CONCLUDED
                    });

                    this.snackBar.showSuccessSnackbar(this.translate.instant('PROFILE.SUCCESS_MESSAGE'));
                }
            });
    }
    
    public getAdminDetails(): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.get$.next();
    }

    public updateAdmin(admin: Partial<User>): void
    {
        this.state.set({
            ...this.state(),
            status: RequestStatus.PENDING
        });

        this.update$.next(admin);
    }

    public clearAdmin(): void
    {
        this.state.set({
            status: RequestStatus.NOT_EXECUTED,
            user: null
        });
    }
}
